import { Plus, Trash } from "@phosphor-icons/react";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { TONE_CLASSES } from "../../assets/assetUtils";

export const SECTIONS = [
  { n: 1, key: "details", title: "Project & site details" },
  { n: 2, key: "scope", title: "Scope of works" },
  { n: 3, key: "personnel", title: "Personnel & persons at risk" },
  { n: 4, key: "hazards", title: "Hazards & risk assessment" },
  { n: 5, key: "method", title: "Method statement" },
  { n: 6, key: "ppe", title: "PPE" },
  { n: 7, key: "coshh", title: "COSHH, plant & equipment" },
  { n: 8, key: "emergency", title: "Emergency arrangements" },
  { n: 9, key: "monitoring", title: "Permits, supervision & review" },
  { n: 10, key: "signoff", title: "Sign-off & briefing register" },
];

export const RAMS_TONE = { draft: TONE_CLASSES.none, issued: TONE_CLASSES.ok, archived: TONE_CLASSES.none };
export const RAMS_LABEL = { draft: "Draft", issued: "Issued", archived: "Archived" };

export const riskScore = (l, s) => (Number(l) || 0) * (Number(s) || 0);
export const riskBand = (score) => (score >= 15 ? "high" : score >= 5 ? "medium" : "low");
export const RISK_CLASS = {
  low: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  medium: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  high: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};
export const emptyHazard = () => ({ id: Math.random().toString(36).slice(2, 10), hazard: "", who: "", likelihood: 3, severity: 3, controls: "", residual_likelihood: 1, residual_severity: 2 });

export function RiskPill({ l, s, testid }) {
  const score = riskScore(l, s);
  const band = riskBand(score);
  return <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${RISK_CLASS[band]}`} data-testid={testid}>{score} {band.toUpperCase()}</span>;
}

export function Field({ label, value, onChange, placeholder, type = "text", testid, rows, hint }) {
  const Cmp = rows ? Textarea : Input;
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Cmp type={type} rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={rows ? "mt-1 text-sm" : "mt-1 h-9 text-sm"} data-testid={testid} />
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

/** Editable list of one-line strings (method steps, PPE, equipment, persons at risk). */
export function ListEditor({ items, onChange, placeholder, addLabel = "Add", numbered, testid }) {
  const list = items || [];
  const set = (i, v) => onChange(list.map((x, k) => (k === i ? v : x)));
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {list.map((x, i) => (
        <div key={i} className="flex items-start gap-2">
          {numbered && <span className="mt-2 w-5 text-right text-xs font-semibold text-muted-foreground">{i + 1}.</span>}
          <Textarea rows={1} value={x} onChange={(e) => set(i, e.target.value)} placeholder={placeholder} className="min-h-[36px] text-sm" data-testid={`${testid}-item-${i}`} />
          <button type="button" onClick={() => onChange(list.filter((_, k) => k !== i))} className="mt-2 text-muted-foreground hover:text-red-600" aria-label="Remove" data-testid={`${testid}-remove-${i}`}><Trash size={14} /></button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => onChange([...list, ""])} data-testid={`${testid}-add`}><Plus size={12} className="mr-1" /> {addLabel}</Button>
    </div>
  );
}

export function SectionCard({ n, title, children, id, hint }) {
  return (
    <section id={id || `rams-s${n}`} className="scroll-mt-24 rounded-lg border border-border bg-card p-4 md:p-5" data-testid={`rams-section-${n}`}>
      <div className="flex items-baseline gap-3 mb-3">
        <span className="font-[Chivo] text-2xl font-bold text-primary/80">{n}.</span>
        <div><h2 className="text-base font-semibold">{title}</h2>{hint && <p className="text-xs text-muted-foreground">{hint}</p>}</div>
      </div>
      {children}
    </section>
  );
}
